import React, {createContext, useContext, useMemo, useState} from 'react';
import {HSPackedMedication} from 'server-openapi';

type SelectionMap = Record<string, HSPackedMedication[]>;

type Ctx = {
    getSelection: (scopeId: string) => HSPackedMedication[];
    setSelection: (scopeId: string, meds: HSPackedMedication[]) => void;
    clearSelection: (scopeId: string) => void;
};

const SyringeSelectionContext = createContext<Ctx | undefined>(undefined);

export function SyringeSelectionProvider({children}: {children: React.ReactNode}) {
    const [map, setMap] = useState<SelectionMap>({});

    const value = useMemo<Ctx>(() => ({
        getSelection: (scopeId) => map[scopeId] ?? [],
        setSelection: (scopeId, meds) => setMap((m) => ({...m, [scopeId]: meds})),
        clearSelection: (scopeId) => setMap((m) => {
            const copy = {...m};
            delete copy[scopeId];
            return copy;
        })
    }), [map]);

    return (
        <SyringeSelectionContext.Provider value={value}>
            {children}
        </SyringeSelectionContext.Provider>
    );
}

export function useSyringeSelection(scopeId: string) {
    const ctx = useContext(SyringeSelectionContext);
    if (!ctx) throw new Error('useSyringeSelection must be used within SyringeSelectionProvider');
    return {
        selected: ctx.getSelection(scopeId),
        setSelected: (meds: HSPackedMedication[]) => ctx.setSelection(scopeId, meds),
        clear: () => ctx.clearSelection(scopeId),
    };
}
